import { type ExtendToCellEvent } from '../events/types';
import { type SelectionRange, type SelectionState } from '../state/types';

import { type ReducerResponse } from './types';

/**
 * Handles extending a selection to a cell (e.g., dragging or Shift+Click).
 *
 * This reducer should only be called when:
 * - Mode is 'cell'
 * - There's already a selection in the selections array
 *
 * For cell selections, there should only be one selection in the array.
 * The focusedCell should be the anchor cell in the range.
 */
export function extendToCellReducer(
  state: SelectionState,
  event: ExtendToCellEvent
): ReducerResponse {
  if (state.mode !== 'cell') {
    throw new Error(
      `extendToCell called when mode is '${state.mode}'. This should never happen - extendToCell should only be called in 'cell' mode.`
    );
  }

  if (state.selections.length !== 1) {
    throw new Error(
      `extendToCell called with selections count: ${state.selections.length}. For extendToCell only one selectionRange should be present.`
    );
  }

  // For cell selections, there's only one range, so we can directly access it
  const cellSelection = state.selections[0];

  // If the lead is already at the target cell, no change needed
  if (
    cellSelection.lead.row.id === event.cell.row.id &&
    cellSelection.lead.col.id === event.cell.col.id
  ) {
    return { next: state, delta: { kind: 'noop' } };
  }

  // For cell selections, there's only one range, so we can directly update it
  const updatedSelection: SelectionRange = {
    ...cellSelection,
    lead: event.cell,
  };

  return {
    delta: {
      kind: 'selectionModified',
      source: event.source,
      updated: [updatedSelection],
    },
    next: {
      ...state,
      selections: [updatedSelection],
      lastInteractionSource: event.source,
    },
  };
}
